import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import NodeFieldLabel from './NodeFieldLabel';
import { usePortaledMenu } from './usePortaledMenu';
import { useListboxKeys } from './useListboxKeys';
import {
  withDescriptionTooltip,
  type SelectFieldOption,
} from './selectFieldOption';
import {
  SelectBtn,
  SelectCaret,
  SelectEmpty,
  SelectInput,
  SelectInputCaret,
  SelectMenu,
  SelectOption,
  SelectValue,
  SelectWrap,
} from './nodeCommon.styled';

export type { SelectFieldOption };

export interface SelectFieldProps {
  /**
   * Optional label rendered above the control. Omit for inline pickers
   * (per-port kind picker on {@link OutputRow}) where a sibling label
   * already exists; supply for the catalog-driven body fields rendered
   * by {@link ConfigFieldRenderer}.
   */
  readonly label?: string;
  /** Optional explanatory copy surfaced as a hover tooltip on {@link label}. */
  readonly hint?: string | null;
  /** Test id for the label's tooltip; only used when {@link hint} is set. */
  readonly hintTestId?: string;
  readonly options: readonly SelectFieldOption[];
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly readOnly?: boolean;
  /** Shown (muted) on the trigger when no option is selected yet. */
  readonly placeholder?: string;
  /** ARIA label — defaults to {@link label} when present. Required when
   *  no visible {@link label} so screen-reader users still have context. */
  readonly ariaLabel?: string;
  /** Element {@code data-testid} on the trigger button; option buttons
   *  get a derived test id of the form {@code `${testId}__option-VALUE`}. */
  readonly testId: string;
  /**
   * Opt-in: render a filter input at the top of the open menu and narrow
   * the option list as the user types (case-insensitive label match).
   * Defaults off so the short config / per-port pickers stay unchanged.
   * Use for long, dynamic lists like the fal.ai image-model catalog.
   */
  readonly searchable?: boolean;
  /** Placeholder for the search input; only used when {@link searchable}. */
  readonly searchPlaceholder?: string;
}

/**
 * Bare-bones pill-style dropdown used both by the body
 * {@link ConfigFieldRenderer} ({@code SELECT} fields) and by the
 * per-port kind picker in {@link OutputRow}. Generic on options +
 * value so the same component covers any string-keyed enum without
 * dragging the {@code ConfigFieldDto} dependency into call sites that
 * don't have one (e.g. the per-port picker is wired off
 * {@code data.ports[*].portType}, not a catalog config field).
 *
 * <p>The open menu is portaled to {@code document.body} and positioned
 * imperatively off the trigger's rect so it escapes the node card's
 * {@code overflow} clipping and follows scroll/resize.</p>
 */
const SelectField: React.FC<SelectFieldProps> = ({
  label,
  hint,
  hintTestId,
  options,
  value,
  onChange,
  readOnly = false,
  placeholder,
  ariaLabel,
  testId,
  searchable = false,
  searchPlaceholder,
}) => {
  const [query, setQuery] = useState('');
  // Clears the filter on a fresh open; the guard inside the hook keeps a
  // re-focus of an already-open combobox from wiping what the user has typed.
  const onOpen = useCallback(() => setQuery(''), []);
  const { open, wrapRef, menuRef, openMenu, closeMenu } = usePortaledMenu(onOpen);

  const visibleOptions = useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [searchable, options, query]);

  const currentOption = options.find((o) => o.value === value);
  const isEmpty = value == null || value === '';
  const showPlaceholder = isEmpty && !!placeholder;
  const currentLabel = showPlaceholder ? placeholder : currentOption?.label ?? value;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const labelForAria = ariaLabel ?? label;

  const select = (next: string) => {
    onChange(next);
    closeMenu();
  };

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxKeys({
    itemCount: visibleOptions.length,
    open,
    openMenu,
    closeMenu,
    onCommit: (index) => {
      const opt = visibleOptions[index];
      if (opt) select(opt.value);
    },
    // On the button trigger, an unhandled Space falls through to the native
    // click and toggles the menu shut without a pick.
    spaceCommits: !searchable,
    // Searching narrows the list, so the top match is the useful start there;
    // an unfiltered list opens on whatever is currently selected.
    initialIndex: searchable
      ? 0
      : Math.max(0, options.findIndex((o) => o.value === value)),
  });
  const activeOption = open ? visibleOptions[activeIndex] : undefined;
  const activeOptionId = activeOption
    ? `${testId}__option-${activeOption.value}`
    : undefined;
  // Only referenceable while the portal is mounted.
  const menuId = `${testId}__menu`;
  const controls = open ? menuId : undefined;

  // role="combobox" over the native button role, which supports neither
  // aria-activedescendant nor aria-controls: the option the arrow keys move
  // over lives in the portaled listbox, not under the trigger.
  const trigger = (
    <SelectBtn
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={labelForAria}
      aria-activedescendant={activeOptionId}
      data-testid={testId}
      disabled={readOnly}
      onPointerDown={stop}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        stop(e);
        if (readOnly) return;
        if (open) closeMenu();
        else openMenu();
      }}
    >
      <SelectValue $placeholder={showPlaceholder}>{currentLabel}</SelectValue>
      <SelectCaret className="material-symbols-rounded" aria-hidden="true">
        expand_more
      </SelectCaret>
    </SelectBtn>
  );

  // Searchable: the pill is a text input. Closed, it shows the current label;
  // open, it clears so the user types in place, with the selection kept as a
  // placeholder for reference.
  const comboboxTrigger = (
    <>
      <SelectInput
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={controls}
        aria-label={labelForAria}
        aria-activedescendant={activeOptionId}
        data-testid={testId}
        disabled={readOnly}
        value={open ? query : currentLabel}
        placeholder={open ? currentLabel : (searchPlaceholder ?? 'Search…')}
        onPointerDown={stop}
        onFocus={openMenu}
        onClick={(e) => {
          stop(e);
          openMenu();
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
        }}
        onKeyDown={onKeyDown}
      />
      <SelectInputCaret
        className="material-symbols-rounded"
        aria-hidden="true"
        $open={open}
      >
        expand_more
      </SelectInputCaret>
    </>
  );

  return (
    <div>
      {label ? (
        <NodeFieldLabel label={label} hint={hint} dataTestId={hintTestId} />
      ) : null}
      <SelectWrap ref={wrapRef}>
        {searchable
          ? comboboxTrigger
          : withDescriptionTooltip(currentOption, trigger)}
        {open &&
          createPortal(
            <SelectMenu
              ref={menuRef}
              id={menuId}
              role="listbox"
              $portal
              onPointerDown={stop}
            >
              {visibleOptions.length === 0 && (
                <SelectEmpty data-testid={`${testId}__empty`}>
                  No matches
                </SelectEmpty>
              )}
              {visibleOptions.map((opt, index) => {
                const option = (
                  <SelectOption
                    type="button"
                    id={`${testId}__option-${opt.value}`}
                    role="option"
                    aria-selected={opt.value === value}
                    $active={opt.value === value}
                    $highlighted={index === activeIndex}
                    data-testid={`${testId}__option-${opt.value}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={(e) => {
                      stop(e);
                      select(opt.value);
                    }}
                  >
                    {opt.label}
                  </SelectOption>
                );
                return (
                  <React.Fragment key={opt.value}>
                    {withDescriptionTooltip(opt, option)}
                  </React.Fragment>
                );
              })}
            </SelectMenu>,
            document.body,
          )}
      </SelectWrap>
    </div>
  );
};

export default SelectField;
